import React, { useState, useEffect, useRef } from "react";

import { useCompass } from "../contexts/CompassContext";

interface GridOverlayProps {
  enabled?: boolean;
}

export default function GridOverlay({
  enabled: externalEnabled,
}: GridOverlayProps) {
  const [enabled, setEnabled] = useState(false);
  const { state: compassState } = useCompass();
  const [gridInfo, setGridInfo] = useState<{
    left: number;
    width: number;
    paddingLeft: number;
    paddingRight: number;
    columnWidth: number;
    gutterWidth: number;
  } | null>(null);
  const gridContainerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const findAndMeasureGrid = () => {
      // Find the grid container
      const gridContainer = document.querySelector(
        ".retailer-12col-grid.mx-auto"
      ) as HTMLElement;

      if (!gridContainer) {
        setGridInfo(null);
        return;
      }

      gridContainerRef.current = gridContainer;
      const rect = gridContainer.getBoundingClientRect();
      const computedStyle = window.getComputedStyle(gridContainer);

      // Get actual padding values
      const paddingLeft = parseFloat(computedStyle.paddingLeft) || 48;
      const paddingRight = parseFloat(computedStyle.paddingRight) || 48;

      // Get the computed gap value (CSS Grid uses 'gap' property)
      const computedGap =
        computedStyle.gap || computedStyle.columnGap || "16px";
      // Gap can be "16px" or "16px 16px" - extract the first value
      const gapMatch = computedGap.match(/(\d+(?:\.\d+)?)px/);
      const gutterWidth = gapMatch ? parseFloat(gapMatch[1]) : 16;

      // Calculate available width for columns (outer width minus padding)
      const availableWidth = rect.width - paddingLeft - paddingRight;

      // CSS Grid with repeat(12, 1fr) and gap calculates:
      // availableWidth = (12 * columnWidth) + (11 * gap)
      // So: columnWidth = (availableWidth - (11 * gap)) / 12
      const numColumns = 12;
      const numGutters = numColumns - 1;
      const calculatedColumnWidth =
        (availableWidth - numGutters * gutterWidth) / numColumns;

      // Try to measure an actual grid item to verify
      let measuredColumnWidth = calculatedColumnWidth;
      const gridItems = Array.from(gridContainer.children) as HTMLElement[];
      if (gridItems.length > 0) {
        // Find an item that spans exactly one column
        const singleColumnItem = gridItems.find((item) => {
          const itemStyle = window.getComputedStyle(item);
          const gridColumn =
            itemStyle.gridColumnStart && itemStyle.gridColumnEnd
              ? parseInt(itemStyle.gridColumnEnd) -
                parseInt(itemStyle.gridColumnStart)
              : 0;
          return gridColumn === 1;
        });

        if (singleColumnItem) {
          const itemRect = singleColumnItem.getBoundingClientRect();
          measuredColumnWidth = itemRect.width;
        }
      }

      // Keep the column width fractional. Rounding to whole pixels here compounds
      // across the 12 columns — worst near a 20px gutter (~5px of drift by the
      // last column) — so the overlay slides out of alignment with the real
      // 1fr-based grid. Using the exact fractional width matches it precisely.
      const actualColumnWidth = measuredColumnWidth;

      setGridInfo({
        left: Math.round(rect.left),
        width: Math.round(rect.width),
        paddingLeft,
        paddingRight,
        columnWidth: actualColumnWidth,
        gutterWidth,
      });
    };

    // Initial find
    findAndMeasureGrid();

    // Update on resize and panel state changes
    const handleResize = () => {
      setTimeout(findAndMeasureGrid, 50);
    };

    window.addEventListener("resize", handleResize);

    // Watch for DOM changes
    const observer = new MutationObserver(() => {
      setTimeout(findAndMeasureGrid, 50);
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "class"],
    });

    // Poll for updates (especially when panel opens/closes)
    const interval = setInterval(findAndMeasureGrid, 200);

    return () => {
      window.removeEventListener("resize", handleResize);
      observer.disconnect();
      clearInterval(interval);
    };
  }, [compassState.isPanelOpen]);

  useEffect(() => {
    // Keyboard shortcut: 'Shift + G' to toggle grid
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isInput =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target.isContentEditable ||
        target.closest('input, textarea, [contenteditable="true"]');

      if (
        e.key.toLowerCase() === "g" &&
        e.shiftKey &&
        !isInput &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey
      ) {
        e.preventDefault();
        setEnabled((prev) => !prev);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const isEnabled = externalEnabled !== undefined ? externalEnabled : enabled;

  if (!isEnabled || !gridInfo || gridInfo.width <= 0) {
    return null;
  }

  const { left, width, paddingLeft, paddingRight, columnWidth, gutterWidth } =
    gridInfo;
  const numColumns = 12;

  // Position overlay where columns actually start (grid container left + 48px padding)
  // This ensures 48px margin on the left
  const overlayLeft = Math.round(left + paddingLeft);
  // Width is just the column area (total width minus both 48px paddings)
  // This ensures 48px margin on the right
  const overlayWidth = Math.round(width - paddingLeft - paddingRight);

  // Verify: overlay should end at (left + width - paddingRight) to maintain 48px right margin
  const overlayRight = overlayLeft + overlayWidth;
  const expectedRight = Math.round(left + width - paddingRight);

  return (
    <div
      className="pointer-events-none fixed top-0 bottom-0 z-9999"
      style={{
        left: `${overlayLeft}px`,
        width: `${overlayWidth}px`,
      }}
    >
      {/* Columns are positioned directly, no inner padding needed */}
      <div
        className="relative h-full"
        style={{
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {/* Column backgrounds */}
        {Array.from({ length: numColumns }).map((_, index) => {
          const leftPosition = index * (columnWidth + gutterWidth);
          return (
            <div
              key={`col-${index}`}
              className="absolute top-0 bottom-0"
              style={{
                left: `${leftPosition}px`,
                width: `${columnWidth}px`,
                backgroundColor: "rgba(255, 0, 0, 0.15)",
              }}
            />
          );
        })}
      </div>

      {/* Info badge — only present while the grid layer is on (Shift+G) */}
      <div
        className="text-fs-text-primary-inverse pointer-events-auto fixed right-4 bottom-4 rounded-lg border border-(--slate-color-grey-100)/10 bg-(--slate-color-grey-1000)/80 px-3.5 py-3 shadow-lg backdrop-blur-md"
        style={{ zIndex: 10000 }}
      >
        <div className="text-fs-text-primary-inverse/50 mb-2 text-[10px] font-medium tracking-wider uppercase">
          Grid overlay
        </div>
        <dl className="flex flex-col gap-1 text-xs">
          <div className="flex items-center justify-between gap-8">
            <dt className="text-fs-text-primary-inverse/55">Columns</dt>
            <dd className="tabular-nums">{numColumns}</dd>
          </div>
          <div className="flex items-center justify-between gap-8">
            <dt className="text-fs-text-primary-inverse/55">Gutter</dt>
            <dd className="tabular-nums">{gutterWidth}px</dd>
          </div>
          <div className="flex items-center justify-between gap-8">
            <dt className="text-fs-text-primary-inverse/55">Margins</dt>
            <dd className="tabular-nums">
              {paddingLeft} / {paddingRight}
            </dd>
          </div>
        </dl>
        <div className="text-fs-text-primary-inverse/45 mt-2.5 flex items-center gap-1.5 border-t border-(--slate-color-grey-100)/10 pt-2 text-[10px]">
          <kbd className="rounded border border-(--slate-color-grey-100)/20 px-1 py-0.5 font-sans">
            ⇧G
          </kbd>
          to toggle
        </div>
      </div>
    </div>
  );
}
